import { useEffect, useId, useState } from 'react'
import api from '../api/client'
import { LOCALIDADES_BA } from '../data/argentina'
import { colors, font, text, space, radius } from '../design/tokens'
import Boton from './Boton'

/*
 * Campo de localidad inteligente ("¿quisiste decir?").
 *
 * - Input libre con autocompletado (<datalist>) contra las localidades conocidas
 *   de la provincia (GET /api/localidades, cacheado por provincia).
 * - onBlur:
 *     · coincidencia EXACTA (case+tildes-insensible) → usa la canónica, sin preguntar.
 *     · parecida (match difuso) → ofrece inline "¿Quisiste decir X?" con
 *       [Usar esta] (corrige el typo) / [Dejar "lo escrito"] (entra como nueva).
 *     · sin parecido → se acepta como nueva (hint sutil).
 * - La localidad nueva se aprende en backend al guardar la dirección (el server
 *   la suma a la tabla `localidades` y aparece la próxima vez para todos).
 *
 * El componente asume que se usa para provincias que SÍ tienen localidad
 * (la pantalla decide no renderizarlo para CABA, que va por CP).
 */

// Cache de listas por provincia (módulo, vive mientras dure la sesión SPA).
const cacheListas = {}

const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()

// Distancia de Levenshtein.
function levenshtein(a, b) {
  const m = a.length
  const n = b.length
  if (m === 0) return n
  if (n === 0) return m
  let prev = Array.from({ length: n + 1 }, (_, i) => i)
  let cur = new Array(n + 1)
  for (let i = 1; i <= m; i++) {
    cur[0] = i
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost)
    }
    ;[prev, cur] = [cur, prev]
  }
  return prev[n]
}

// Similitud normalizada [0..1] = 1 - distancia/largo del más largo.
function similitud(a, b) {
  const na = norm(a)
  const nb = norm(b)
  if (!na || !nb) return 0
  if (na === nb) return 1
  const max = Math.max(na.length, nb.length)
  return 1 - levenshtein(na, nb) / max
}

const UMBRAL = 0.72 // a partir de acá se ofrece "¿quisiste decir?"

export default function CampoLocalidad({ provincia, value, onChange, inputStyle, placeholder = 'Localidad', required = false, cp = null }) {
  const [lista, setLista] = useState([])
  const [sugerencia, setSugerencia] = useState(null) // candidata para "¿quisiste decir?"
  const [esNueva, setEsNueva] = useState(false)
  const listaId = useId()

  // Carga la lista de la provincia (cacheada). Fallback a LOCALIDADES_BA para PBA.
  useEffect(() => {
    let vivo = true
    setSugerencia(null)
    setEsNueva(false)
    if (!provincia) { setLista([]); return }
    if (cacheListas[provincia]) { setLista(cacheListas[provincia]); return }
    if (provincia === 'Buenos Aires') setLista(LOCALIDADES_BA) // mientras llega el fetch
    api.get('/localidades', { params: { provincia } })
      .then(({ data }) => {
        if (!vivo) return
        const arr = Array.isArray(data) ? data : []
        const final = arr.length ? arr : (provincia === 'Buenos Aires' ? LOCALIDADES_BA : [])
        cacheListas[provincia] = final
        setLista(final)
      })
      .catch(() => {
        if (vivo) setLista(provincia === 'Buenos Aires' ? LOCALIDADES_BA : [])
      })
    return () => { vivo = false }
  }, [provincia])

  // Sugerencia por CP: cuando llega un CP de 4 dígitos y la localidad está
  // vacía, la completa con la más frecuente entre los clientes con ese CP
  // (GET /localidades/por-cp), canonicalizada contra la lista de la provincia.
  // No pisa una localidad ya escrita/elegida a mano.
  useEffect(() => {
    const limpio = String(cp || '').replace(/\D/g, '').slice(0, 4)
    if (limpio.length < 4) return
    if (value && value.trim()) return
    let vivo = true
    api.get('/localidades/por-cp', { params: { cp: limpio } })
      .then(({ data }) => {
        if (!vivo || !data?.localidad) return
        const canon = lista.find(l => norm(l) === norm(data.localidad)) || data.localidad
        onChange(canon)
      })
      .catch(() => { /* sin datos para ese CP: no pasa nada */ })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cp, value, lista])

  // Mejor candidato canónico para el valor actual (exacto o difuso).
  const evaluar = (val) => {
    const v = String(val || '').trim()
    if (!v) return { canonica: '', sugerencia: null, nueva: false }
    const exacta = lista.find(l => norm(l) === norm(v))
    if (exacta) return { canonica: exacta, sugerencia: null, nueva: false }

    let mejor = null
    let mejorSim = 0
    for (const l of lista) {
      const s = similitud(v, l)
      if (s > mejorSim) { mejorSim = s; mejor = l }
    }
    if (mejor && mejorSim >= UMBRAL) {
      return { canonica: v, sugerencia: mejor, nueva: false }
    }
    return { canonica: v, sugerencia: null, nueva: true }
  }

  const handleBlur = () => {
    const r = evaluar(value)
    if (r.canonica !== value && r.canonica) onChange(r.canonica)
    setSugerencia(r.sugerencia)
    setEsNueva(r.nueva)
  }

  const aceptarSugerencia = () => {
    if (sugerencia) onChange(sugerencia)
    setSugerencia(null)
    setEsNueva(false)
  }

  const dejarEscrito = () => {
    setSugerencia(null)
    setEsNueva(true)
  }

  return (
    <div>
      <input
        list={listaId}
        style={inputStyle}
        value={value || ''}
        required={required}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setSugerencia(null); setEsNueva(false) }}
        onBlur={handleBlur}
      />
      <datalist id={listaId}>
        {lista.map(l => <option key={l} value={l} />)}
      </datalist>

      {sugerencia && (
        <div style={sx.sug}>
          <span style={sx.sugTxt}>¿Quisiste decir <strong>{sugerencia}</strong>?</span>
          <div style={sx.sugBtns}>
            <Boton variante="success" sm type="button" onMouseDown={e => { e.preventDefault(); aceptarSugerencia() }}>Usar esta</Boton>
            <Boton variante="ghost" sm type="button" onMouseDown={e => { e.preventDefault(); dejarEscrito() }}>Dejar "{value}"</Boton>
          </div>
        </div>
      )}

      {!sugerencia && esNueva && value?.trim() && (
        <div style={sx.nueva}>Se agregará como localidad nueva.</div>
      )}
    </div>
  )
}

const sx = {
  sug: {
    marginTop: space.xs,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: space.md,
    padding: `${space.sm}px ${space.md}px`,
    background: colors.warnBg,
    border: `1px solid ${colors.warn}33`,
    borderRadius: radius.sm,
    fontFamily: font.sans,
  },
  sugTxt: { fontSize: text.sec, color: colors.warn },
  sugBtns: { display: 'flex', gap: space.sm, marginLeft: 'auto' },
  nueva: {
    marginTop: space.xs,
    fontSize: text.meta,
    color: colors.textMeta,
    fontFamily: font.sans,
  },
}
